import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { MOUNTAIN_LISTS } from '../data/mountainData';
import { isProfileDisclosureValid } from '../utils/profileDisclosure';
import { notificationService } from '../services/notificationService';
import { toPlainText } from '../utils/plainText';
import { showToast } from '../utils/toast';
import './Profile.css';

const REPORT_REASON_OPTIONS = [
  { value: 'violence_threat', label: '暴力・脅迫' },
  { value: 'romance_solicitation', label: 'ナンパ・恋愛目的の勧誘' },
  { value: 'dangerous_hiking_plan', label: '危険な登山計画・行動' },
  { value: 'last_minute_cancel_or_nuisance', label: 'ドタキャン・迷惑行為' },
  { value: 'impersonation_false_info', label: 'なりすまし・虚偽情報' },
  { value: 'personal_info_abuse', label: '個人情報悪用' },
  { value: 'other', label: 'その他' }
];

const REPORT_RECEIVED_MESSAGE = '通報を受け付けました。通報内容を確認してから、原則24時間以内に一次判断、最大48時間以内に確定判断で対応します。開発者X（https://x.com/yamaloglist）でもDM頂けますと状況確認が早くできますので、ご協力お願いいたします。';

function UserProfile() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isBlocked, setIsBlocked] = useState(false);
  const [isBlockedBy, setIsBlockedBy] = useState(false);
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportComment, setReportComment] = useState('');
  const [climbedMountains, setClimbedMountains] = useState([]);
  const [canSeePrivate, setCanSeePrivate] = useState(false);
  const [userStats, setUserStats] = useState({});
  const [reviews, setReviews] = useState([]);

  const isOwnProfile = currentUser?.uid === uid;

  const formatMembershipDuration = (createdAt) => {
    const createdDate = createdAt?.toDate ? createdAt.toDate() : createdAt ? new Date(createdAt) : null;
    if (!createdDate || Number.isNaN(createdDate.getTime())) return '不明';
    const diffDays = Math.max(0, Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24)));
    if (diffDays < 30) return `${diffDays + 1}日`;
    const months = Math.floor(diffDays / 30);
    if (months < 12) return `${months}ヶ月`;
    const years = Math.floor(months / 12);
    const restMonths = months % 12;
    return restMonths ? `${years}年${restMonths}ヶ月` : `${years}年`;
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const [userDoc, statsDoc, reviewsSnap, myBlockDoc, blockedByDoc] = await Promise.all([
          getDoc(doc(db, 'users', uid)),
          getDoc(doc(db, 'userStats', uid)),
          getDocs(query(collection(db, 'reviews'), where('revieweeId', '==', uid), orderBy('createdAt', 'desc'), limit(20))),
          currentUser ? getDoc(doc(db, 'blocks', `${currentUser.uid}_${uid}`)) : Promise.resolve(null),
          currentUser ? getDoc(doc(db, 'blocks', `${uid}_${currentUser.uid}`)) : Promise.resolve(null),
        ]);

        if (userDoc.exists()) {
          setProfile(userDoc.data());
        } else {
          const fallbackRecruitments = await getDocs(
            query(collection(db, 'recruitments'), where('userId', '==', uid), limit(1))
          );
          if (!fallbackRecruitments.empty) {
            const recruitmentData = fallbackRecruitments.docs[0].data();
            if (recruitmentData.organizer) {
              setProfile(recruitmentData.organizer);
            }
          }
        }
        setUserStats(statsDoc.exists() ? statsDoc.data() : {});
        setReviews(reviewsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        setIsBlocked(!!myBlockDoc?.exists?.());
        setIsBlockedBy(!!blockedByDoc?.exists?.());

        if (currentUser && !isOwnProfile && !myBlockDoc?.exists?.() && !blockedByDoc?.exists?.()) {
          const disclosureSnap = await getDocs(
            query(
              collection(db, 'profileDisclosures'),
              where('ownerId', '==', uid),
              where('viewerId', '==', currentUser.uid),
              where('active', '==', true),
              limit(50)
            )
          );
          const validity = await Promise.all(
            disclosureSnap.docs.map(d => isProfileDisclosureValid(d.data()))
          );
          setCanSeePrivate(validity.some(Boolean));
        } else {
          setCanSeePrivate(false);
        }
      } catch (err) {
        console.error('プロフィールの取得に失敗しました:', err);
      } finally {
        setLoading(false);
      }

      // 登山ログは独立して取得（失敗してもプロフィール表示に影響しない）
      try {
        const logsSnap = await getDocs(collection(db, 'users', uid, 'mountainLogs'));
        const names = [];
        logsSnap.forEach(logDoc => {
          const data = logDoc.data();
          if (data.climbed) {
            const mountain = MOUNTAIN_LISTS[data.listId]?.mountains?.find(m => m.no === data.no);
            if (mountain) names.push(mountain.name);
          }
        });
        setClimbedMountains(names);
      } catch (_) {}
    };

    fetchProfile();
  }, [uid, currentUser, isOwnProfile]);

  const handleStartChat = async () => {
    if (!currentUser) {
      showToast({ message: 'チャットするにはログインが必要です。', type: 'info' });
      navigate('/login');
      return;
    }

    if (isOwnProfile) return;

    const myName = currentUser.displayName || '名無しの登山者';
    const otherName = profile?.nickname || profile?.displayName || '名無しの登山者';
    const chatId = [currentUser.uid, uid].sort().join('_');
    const chatRef = doc(db, 'chats', chatId);
    const chatDoc = await getDoc(chatRef);

    if (!chatDoc.exists()) {
      await setDoc(chatRef, {
        participants: [currentUser.uid, uid],
        organizerId: '',
        recruitmentId: '',
        recruitmentName: '',
        participantNames: {
          [currentUser.uid]: myName,
          [uid]: otherName,
        },
        lastMessage: '',
        lastMessageAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      });
    }

    navigate(`/chat/${chatId}`);
  };

  const handleToggleBlock = async () => {
    if (!currentUser) {
      showToast({ message: 'ブロックするにはログインが必要です。', type: 'info' });
      navigate('/login');
      return;
    }

    if (isOwnProfile) return;

    const blockRef = doc(db, 'blocks', `${currentUser.uid}_${uid}`);

    try {
      if (isBlocked) {
        if (!window.confirm('ブロックを解除しますか？')) return;
        await deleteDoc(blockRef);
        setIsBlocked(false);
      } else {
        if (!window.confirm('このユーザーをブロックしますか？')) return;
        await setDoc(blockRef, {
          blockerId: currentUser.uid,
          blockedId: uid,
          createdAt: serverTimestamp(),
        });
        setIsBlocked(true);
      }
    } catch (err) {
      console.error('ブロック操作に失敗しました:', err);
      showToast({ message: 'ブロック操作に失敗しました。', type: 'error' });
    }
  };

  const handleReportUser = async () => {
    if (!currentUser) {
      showToast({ message: '通報するにはログインが必要です。', type: 'info' });
      navigate('/login');
      return;
    }

    if (!reportReason) {
      showToast({ message: '通報理由を選択してください。', type: 'error' });
      return;
    }

    try {
      await addDoc(collection(db, 'reports'), {
        type: 'user',
        targetId: uid,
        targetName: profile?.nickname || profile?.displayName || 'ユーザー',
        reporterId: currentUser.uid,
        reason: reportReason,
        comment: reportComment,
        decisionStatus: 'pending',
        reviewedBy: '',
        reviewedAt: null,
        actionTaken: 'none',
        createdAt: serverTimestamp(),
      });
      await notificationService.create(currentUser.uid, 'report_received', {
        message: REPORT_RECEIVED_MESSAGE,
        targetType: 'user',
        targetId: uid,
        targetName: profile?.nickname || profile?.displayName || 'ユーザー',
      });
      showToast({ message: '通報を受け付けました。運営が確認します。', type: 'success' });
      setShowReportForm(false);
      setReportReason('');
      setReportComment('');
    } catch (err) {
      console.error('通報に失敗しました:', err);
      showToast({ message: '通報に失敗しました。', type: 'error' });
    }
  };

  if (loading) return <div className="loading">読み込み中...</div>;

  if (!profile) {
    return (
      <div className="profile-container">
        <button className="btn btn-outline" onClick={() => navigate(-1)}>← 戻る</button>
        <p style={{ marginTop: '20px' }}>プロフィールが見つかりません。</p>
      </div>
    );
  }

  const shouldHideProfileDetails = !isOwnProfile && (isBlocked || isBlockedBy);

  return (
    <div className="profile-container">
      <div className="profile-header">
        <button className="btn btn-outline" onClick={() => navigate(-1)} style={{ marginBottom: '16px' }}>
          ← 戻る
        </button>
        <h1>プロフィール</h1>
      </div>

      <div className="profile-content">
        <div className="main-info">
          <div className="card">
            <div className="card-header">
              <h2>基本情報</h2>
            </div>

            <div className="card-body">
              {profile?.photoURL && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                  <img
                    src={profile.photoURL}
                    alt="プロフィール"
                    style={{ width: '96px', height: '96px', borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--primary-green)' }}
                  />
                </div>
              )}

              <div className="info-grid">
                <div className="info-item">
                  <label>ニックネーム</label>
                  <p>{profile?.nickname || profile?.displayName || '名無しの登山者'}</p>
                </div>
                <div className="info-item">
                  <label>登録日</label>
                  <p>
                    {profile?.createdAt
                      ? new Date(profile.createdAt.toDate ? profile.createdAt.toDate() : profile.createdAt).toLocaleDateString('ja-JP', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })
                      : '不明'}
                  </p>
                </div>
              </div>

              {shouldHideProfileDetails ? (
                <div className="profile-section">
                  <p className="review-unavailable">ブロック関係にあるため、プロフィール情報は表示できません。</p>
                </div>
              ) : (
                <>
                  <div className="public-profile-section">
                    <div className="public-profile-header">
                      <label>参加実績・継続期間</label>
                    </div>
                    <div className="public-profile-grid">
                      <div className="public-profile-item">
                        <label>参加実績</label>
                        <p>{userStats.completedHikeCount || 0}回</p>
                      </div>
                      <div className="public-profile-item">
                        <label>応募数</label>
                        <p>{userStats.appliedCount || 0}回</p>
                      </div>
                      <div className="public-profile-item">
                        <label>募集作成数</label>
                        <p>{userStats.recruitedCount || 0}回</p>
                      </div>
                      <div className="public-profile-item">
                        <label>継続期間</label>
                        <p>{formatMembershipDuration(profile?.createdAt)}</p>
                      </div>
                    </div>
                  </div>

                  {profile?.bio && (
                    <div className="bio-section">
                      <label>自己紹介</label>
                      <p className="bio">{toPlainText(profile.bio)}</p>
                    </div>
                  )}

                  {profile?.climbingHistory && (
                    <div className="history-section">
                      <label>登山歴</label>
                      <p className="history">{toPlainText(profile.climbingHistory)}</p>
                    </div>
                  )}

                  {profile?.favoriteMountains && (
                    <div className="favorites-section">
                      <label>好きな山</label>
                      <p className="favorites">{toPlainText(profile.favoriteMountains)}</p>
                    </div>
                  )}

                  {climbedMountains.length > 0 && (
                    <div className="climbed-section">
                      <label>登頂記録（{climbedMountains.length}座）</label>
                      <ul className="climbed-list">
                        {climbedMountains.map(name => (
                          <li key={name}>{name}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="public-profile-section">
                    <div className="public-profile-header">
                      <label>基本情報</label>
                    </div>
                    <div className="public-profile-grid">
                      <div className="public-profile-item">
                        <label>性別</label>
                        <p>{profile?.gender || '未回答'}</p>
                      </div>
                      <div className="public-profile-item">
                        <label>年代</label>
                        <p>{profile?.ageGroup || '未回答'}</p>
                      </div>
                      <div className="public-profile-item">
                        <label>居住都道府県</label>
                        <p>{profile?.residencePrefecture || '未回答'}</p>
                      </div>
                      <div className="public-profile-item">
                        <label>マイカー</label>
                        <p>{profile?.hasCar ? '所有' : '所有なし'}</p>
                      </div>
                      <div className="public-profile-item">
                        <label>加入登山保険</label>
                        <p>{profile?.mountainInsurance || '未設定'}</p>
                      </div>
                    </div>
                  </div>

                  {canSeePrivate && (
                    <div className="private-profile-section">
                      <div className="private-profile-header">
                        <span className="private-badge">非公開プロフィール</span>
                        <p className="privacy-note">以下は明示的に公開された相手にのみ表示されます</p>
                      </div>
                      <div className="private-profile-grid">
                        {profile.nearestStation && (
                          <div className="private-item">
                            <label>最寄り駅</label>
                            <p>{profile.nearestStation}</p>
                          </div>
                        )}
                        {profile.cocoheriNumber && (
                          <div className="private-item">
                            <label>ヘリコプター救助サービス番号</label>
                            <p>{profile.cocoheriNumber}</p>
                          </div>
                        )}
                      </div>
                      <p className="privacy-note">※ 非公開情報のスクリーンショット撮影、転載、第三者への共有は禁止です。</p>
                    </div>
                  )}

                  {(profile?.monthlyFrequency || profile?.pace || profile?.fitnessLevel || (profile?.hikingStyle || []).length > 0) && (
                    <div className="experience-section" style={{ marginTop: '16px' }}>
                      <label>登山スタイル</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                        {profile.monthlyFrequency && profile.monthlyFrequency !== '未回答' && (
                          <span className="exp-tag exp-tag--checked">頻度: {profile.monthlyFrequency}</span>
                        )}
                        {(profile.hikingStyle || []).map(s => {
                          const labels = { dayHike: '日帰り', hutStay: '山小屋泊', tentStay: 'テント泊', trail: 'トレラン', photo: '写真' };
                          return <span key={s} className="exp-tag exp-tag--checked">{labels[s] || s}</span>;
                        })}
                        {profile.pace && profile.pace !== '未回答' && (
                          <span className="exp-tag exp-tag--checked">ペース: {profile.pace}</span>
                        )}
                        {profile.fitnessLevel && profile.fitnessLevel !== '未回答' && (
                          <span className="exp-tag exp-tag--checked">体力: {profile.fitnessLevel}</span>
                        )}
                      </div>
                    </div>
                  )}

                  {((profile?.altitudeExperience || []).length > 0 || (profile?.technicalExperience || []).length > 0) && (
                    <div className="experience-section" style={{ marginTop: '16px' }}>
                      <label>技術・経験</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                        {(profile.altitudeExperience || []).map(k => {
                          const labels = { lowMountain: '低山（〜1000m）', midMountain: '中山（1000〜2000m）', highMountain: '高山（2000〜3000m）', alpineMountain: '3000m超' };
                          return <span key={k} className="exp-tag exp-tag--checked">{labels[k] || k}</span>;
                        })}
                        {(profile.technicalExperience || []).map(k => {
                          const labels = { chainField: '鎖場経験', rockField: '岩場経験', snowMountain: '雪山経験', crampon: 'アイゼン使用', iceAxe: 'ピッケル使用' };
                          return <span key={k} className="exp-tag exp-tag--checked">{labels[k] || k}</span>;
                        })}
                      </div>
                    </div>
                  )}

                  {(profile?.ownedGear || []).length > 0 && (
                    <div className="experience-section" style={{ marginTop: '16px' }}>
                      <label>所持装備</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                        {(profile.ownedGear || []).map(k => {
                          const labels = { helmet: 'ヘルメット', crampon: 'アイゼン', iceAxe: 'ピッケル', tent: 'テント泊装備', winterGear: '雪山装備' };
                          return <span key={k} className="exp-tag exp-tag--checked">{labels[k] || k}</span>;
                        })}
                      </div>
                    </div>
                  )}

                  {reviews.length > 0 && (
                    <div className="reviews-section">
                      <label>受け取った評価 ({reviews.length}件)</label>
                      <div className="reviews-list">
                        {reviews.map(r => (
                          <div key={r.id} className={`review-item review-${r.rating}`}>
                            <span className="review-rating">{r.rating === 'good' ? '良かった' : '残念だった'}</span>
                            {r.comment && <p className="review-comment">{toPlainText(r.comment)}</p>}
                            <p className="review-date">{r.createdAt?.toDate ? r.createdAt.toDate().toLocaleDateString('ja-JP') : ''}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="sidebar">
          <div className="card">
            <h3>SNSリンク</h3>
            <div className="social-links">
              {profile?.yamapLink && (
                <a href={profile.yamapLink} target="_blank" rel="noopener noreferrer" className="social-link">
                  YAMAP
                </a>
              )}
              {profile?.yamarecoLink && (
                <a href={profile.yamarecoLink} target="_blank" rel="noopener noreferrer" className="social-link">
                  ヤマレコ
                </a>
              )}
              {profile?.twitterLink && (
                <a href={profile.twitterLink} target="_blank" rel="noopener noreferrer" className="social-link">
                  X(Twitter)
                </a>
              )}
              {profile?.instagramLink && (
                <a href={profile.instagramLink} target="_blank" rel="noopener noreferrer" className="social-link">
                  Instagram
                </a>
              )}
              {!profile?.yamapLink && !profile?.yamarecoLink && !profile?.twitterLink && !profile?.instagramLink && (
                <p className="no-links">SNSリンクは登録されていません</p>
              )}
            </div>
          </div>

          {!isOwnProfile && (
            <div className="card">
              <h3>アクション</h3>
              <div className="account-actions">
                <button className="btn btn-outline" onClick={handleStartChat}>
                  チャット
                </button>
                <button className={`btn ${isBlocked ? 'btn-secondary' : 'btn-outline'}`} onClick={handleToggleBlock}>
                  {isBlocked ? 'ブロック解除' : 'ブロック'}
                </button>
                <button className="btn btn-danger" onClick={() => setShowReportForm(v => !v)}>
                  通報
                </button>
              </div>

              {isBlockedBy && (
                <p className="review-unavailable" style={{ marginTop: '12px' }}>このユーザーにブロックされています。</p>
              )}

              {showReportForm && (
                <div style={{ marginTop: '16px' }}>
                  <label className="form-label">通報</label>
                  <select
                    className="form-control"
                    value={reportReason}
                    onChange={(e) => setReportReason(e.target.value)}
                    style={{ marginBottom: '10px' }}
                  >
                    <option value="">理由を選択</option>
                    {REPORT_REASON_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <textarea
                    className="form-control"
                    value={reportComment}
                    onChange={(e) => setReportComment(e.target.value)}
                    placeholder="詳細（任意）"
                    rows={3}
                    style={{ marginBottom: '10px' }}
                  />
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button className="btn btn-danger btn-sm" onClick={handleReportUser}>
                      通報する
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setShowReportForm(false)}>
                      キャンセル
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserProfile;
